import { Match, Participant, Tournament } from '../models';
import { TournamentRepository, ParticipantRepository, MatchRepository } from '../repositories';
import { isValidParticipantCount, getWinnersBracketRounds } from '../../core/utils';

interface GeneratedBracket {
  tournament: Tournament;
  matches: Match[];
}

interface TempMatch {
  tournamentId: string;
  round: number;
  matchNumber: number;
  bracketType: 'winners' | 'losers' | 'grand_final';
  participant1Id?: string;
  participant2Id?: string;
  status: 'pending' | 'in_progress' | 'completed';
  nextMatchIndex?: number;
}

export class GenerateSingleEliminationBracketUseCase {
  constructor(
    private tournamentRepository: TournamentRepository,
    private participantRepository: ParticipantRepository,
    private matchRepository: MatchRepository
  ) {}

  async execute(tournamentId: string): Promise<GeneratedBracket> {
    const tournament = await this.tournamentRepository.getById(tournamentId);
    if (!tournament) {
      throw new Error('Tournament not found');
    }

    if (tournament.status !== 'setup') {
      throw new Error('Tournament has already started or is completed');
    }

    const participants = await this.participantRepository.getByTournamentId(tournamentId);
    
    if (!isValidParticipantCount(participants.length)) {
      throw new Error(
        `Invalid number of participants: ${participants.length}. Must be a power of 2 (2, 4, 8, 16, etc.)`
      );
    }

    if (participants.length < 2) {
      throw new Error('At least 2 participants are required');
    }

    const sortedParticipants = [...participants].sort((a, b) => {
      const seedA = a.seed ?? Infinity;
      const seedB = b.seed ?? Infinity;
      return seedA - seedB;
    });

    const tempMatches: TempMatch[] = [];
    const totalRounds = getWinnersBracketRounds(participants.length);

    // Generate bracket
    this.generateBracket(sortedParticipants, tournamentId, totalRounds, tempMatches);

    // Save and link matches
    const savedMatches = await this.saveAndLinkMatches(tempMatches);

    const updatedTournament = await this.tournamentRepository.update(tournamentId, {
      status: 'in_progress',
    });

    return {
      tournament: updatedTournament,
      matches: savedMatches,
    };
  }

  private generateBracket(
    participants: Participant[],
    tournamentId: string,
    totalRounds: number,
    tempMatches: TempMatch[]
  ): number[][] {
    const matchesByRound: number[][] = [];
    
    // Create matches for each round
    for (let round = 1; round <= totalRounds; round++) {
      const matchesInRound = participants.length / Math.pow(2, round);
      const roundMatches: number[] = [];
      
      for (let matchNum = 0; matchNum < matchesInRound; matchNum++) {
        const matchIndex = tempMatches.length;
        roundMatches.push(matchIndex);
        
        const match: TempMatch = {
          tournamentId,
          round,
          matchNumber: matchNum + 1,
          bracketType: 'winners',
          status: 'pending',
        };

        // First round: assign participants
        if (round === 1) {
          match.participant1Id = participants[matchNum * 2].id;
          match.participant2Id = participants[matchNum * 2 + 1].id;
        }

        tempMatches.push(match);
      }
      
      matchesByRound.push(roundMatches);
    }

    // Link matches: winner goes to next round
    for (let round = 0; round < totalRounds - 1; round++) {
      for (let i = 0; i < matchesByRound[round].length; i++) {
        const currentMatchIndex = matchesByRound[round][i];
        const nextMatchIndex = matchesByRound[round + 1][Math.floor(i / 2)];
        tempMatches[currentMatchIndex].nextMatchIndex = nextMatchIndex;
      }
    }

    return matchesByRound;
  }

  private async saveAndLinkMatches(tempMatches: TempMatch[]): Promise<Match[]> {
    const savedMatches: Match[] = [];
    const indexToIdMap: { [key: number]: string } = {};

    // First pass: save all matches
    for (let i = 0; i < tempMatches.length; i++) {
      const temp = tempMatches[i];
      const saved = await this.matchRepository.create({
        tournamentId: temp.tournamentId,
        round: temp.round,
        matchNumber: temp.matchNumber,
        bracketType: temp.bracketType,
        participant1Id: temp.participant1Id,
        participant2Id: temp.participant2Id,
        status: temp.status,
      });
      savedMatches.push(saved);
      indexToIdMap[i] = saved.id;
    }

    // Second pass: update matches with connections
    for (let i = 0; i < tempMatches.length; i++) {
      const temp = tempMatches[i];
      
      if (temp.nextMatchIndex !== undefined && indexToIdMap[temp.nextMatchIndex]) {
        const updated = await this.matchRepository.update(savedMatches[i].id, {
          nextMatchId: indexToIdMap[temp.nextMatchIndex],
        });
        savedMatches[i] = updated;
      }
    }

    return savedMatches;
  }
}
